import { useCallback, useRef, useState } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { FullView, FullViewContent } from '../../../../components/Modal'
import { ActionButton } from '../../../../components/Button'
import { Check, ChevronBackward } from '@nine-thirty-five/material-symbols-react/rounded'
import { type HabitFormData, useGetHabit, useUpdateHabit } from '@zendia/frontend-shared'
import { axiosRequest } from '../../../../utilities/axiosClient'
import { HabitForm, type HabitFormRef } from '../../../../components/Habits/HabitForm'

export const Route = createFileRoute('/_authenticated/habits/$habitId/edit')({
  component: RouteComponent,
  staticData: { suppressNotifications: true },
  validateSearch: (search): { goalId?: string; milestoneId?: string; fromView?: boolean } => ({
    // Set when the habit was opened from a goal's Your Plan, so leaving returns there, on the
    // same milestone
    goalId: search.goalId as string | undefined,
    milestoneId: search.milestoneId as string | undefined,
    // Set when editing was opened from the habit's own page, so leaving returns there
    fromView: search.fromView === true ? true : undefined,
  }),
})

function RouteComponent() {
  const router = useRouter()
  const { habitId } = Route.useParams()
  const { goalId, milestoneId, fromView } = Route.useSearch()
  const { data: habitResponse, isLoading } = useGetHabit(axiosRequest, habitId)
  const { mutateAsync: updateHabit } = useUpdateHabit(axiosRequest)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>()
  const habitFormRef = useRef<HabitFormRef>(null)

  const habit = habitResponse?.data

  const leave = useCallback(() => {
    if (fromView) {
      router.navigate({
        to: '/habits/$habitId',
        params: { habitId },
        search: { goalId, milestoneId },
        replace: true,
      })
    } else if (goalId) {
      router.navigate({
        to: '/goals/$goalId',
        params: { goalId },
        search: { tab: 'plan', planMilestone: milestoneId },
        replace: true,
      })
    } else {
      router.navigate({ to: '/dashboard', replace: true })
    }
  }, [router, habitId, goalId, milestoneId, fromView])

  const onBack = useCallback(() => {
    if (isSubmitting) return
    leave()
  }, [isSubmitting, leave])

  const onSave = useCallback(
    async (data: HabitFormData) => {
      setIsSubmitting(true)
      setSubmitError(undefined)

      try {
        await updateHabit({
          id: habitId,
          data: {
            ...data,
            // null rather than undefined, so clearing the routine actually lifts the
            // habit out of it instead of reading as "leave the routine alone"
            parentId: data.parentId ?? null,
            lifeDomainId: data.lifeDomainId ?? null,
          },
        })
        leave()
      } catch (error) {
        console.error(error)
        setSubmitError('Failed to update habit')
      } finally {
        setIsSubmitting(false)
      }
    },
    [updateHabit, habitId, leave],
  )

  if (isLoading || !habit) {
    return (
      <FullView
        leftAction={
          <ActionButton
            onClick={onBack}
            label="Back"
            icon={<ChevronBackward className="size-6" />}
          />
        }
        centerContent={<div>Edit Habit</div>}
      >
        <FullViewContent>
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-500">{isLoading ? 'Loading...' : 'Habit not found'}</p>
          </div>
        </FullViewContent>
      </FullView>
    )
  }

  return (
    <FullView
      leftAction={
        <ActionButton
          onClick={onBack}
          label="Cancel"
          icon={<ChevronBackward className="size-6" />}
        />
      }
      centerContent={<div>{habit.isRoutine ? 'Edit Routine' : 'Edit Habit'}</div>}
      rightAction={
        <ActionButton
          primary
          onClick={() => {
            habitFormRef.current?.submit()
          }}
          label="Save changes"
          disabled={isSubmitting}
          icon={<Check className="m-auto size-6" />}
        />
      }
    >
      <FullViewContent>
        <div className="h-full">
          <HabitForm
            ref={habitFormRef}
            initialValues={habit}
            onSubmit={onSave}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        </div>
      </FullViewContent>
    </FullView>
  )
}
